import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Check, ChevronDown, Lock } from 'lucide-react';
import { useState } from 'react';
import { DateInput } from '@/components/date-input';
import { ErrorBanner } from '@/components/form';
import { StatusChip } from '@/components/migration/file-drop';
import { ImportTransactionsCard } from '@/components/migration/qbo-history-card';
import { QuickBooksConnection } from '@/components/migration/quickbooks';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatDate } from '@/lib/format';
import {
  MARKABLE,
  fmtAmount,
  SIDE,
  STATUS_LABEL,
  STEPS,
  progress,
  useMigration,
  type MigrationView,
  type Side,
  type StepStatus,
  type StepView,
} from '@/lib/migration';
import { toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/settings/migration/')({ component: MoveFromQuickBooks });

const TONE: Record<StepStatus, 'success' | 'neutral' | 'warning' | 'info'> = {
  done: 'success',
  skipped: 'neutral',
  todo: 'info',
  attention: 'warning',
};

/** Screen G7 (document 06): the Move from QuickBooks checklist. */
function MoveFromQuickBooks() {
  const orgId = useOrgId();
  const me = useMe();
  const m = useMigration();
  return (
    <>
      <PageHeader
        title="Move from QuickBooks"
        description="Bring a client over from QuickBooks Online at a conversion date: lists, opening balances and open items, then compare both systems month by month."
        actions={
          <Button variant="outline" asChild>
            <Link to="/settings/import">Import data</Link>
          </Button>
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : m.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : m.isError ? (
        <EmptyState title="Could not load the checklist" hint={m.error.message} />
      ) : (
        <div className="flex max-w-5xl flex-col gap-4">
          <QuickBooksConnection />
          <ImportTransactionsCard />
          <Checklist key={orgId} view={m.data} staff={Boolean(me.data?.isStaff)} />
        </div>
      )}
    </>
  );
}

function Checklist({ view, staff }: { view: MigrationView; staff: boolean }) {
  const p = progress(view.steps);
  return (
    <div className="flex max-w-5xl flex-col gap-4">
      <div className="flex items-center gap-3 text-sm" role="status">
        <div className="h-2 w-48 overflow-hidden rounded-full bg-muted" aria-hidden>
          <div
            className="h-full bg-primary"
            style={{ width: `${Math.round((p.done / p.total) * 100)}%` }}
          />
        </div>
        <span>
          {p.done} of {p.total} steps done
        </span>
        {view.status === 'finished' ? (
          <StatusChip tone="success">Finished</StatusChip>
        ) : view.status === 'in_progress' ? (
          <StatusChip tone="info">In progress</StatusChip>
        ) : null}
      </div>
      <ol className="flex flex-col divide-y rounded-lg border bg-card">
        {STEPS.map((meta, i) => {
          const step = view.steps.find((s) => s.key === meta.key)!;
          return (
            <li key={meta.key} className="flex gap-4 px-4 py-4" data-testid={`step-${meta.key}`}>
              <span
                className={cn(
                  'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-medium',
                  step.status === 'done' || step.status === 'skipped'
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'text-muted-foreground',
                )}
                aria-hidden
              >
                {step.status === 'done' || step.status === 'skipped' ? (
                  <Check className="size-4" />
                ) : (
                  i + 1
                )}
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{meta.title}</span>
                  <StatusChip tone={TONE[step.status]}>
                    {STATUS_LABEL[step.status]}
                    {step.manual ? ' (marked)' : ''}
                  </StatusChip>
                </div>
                <p className="text-sm">{step.detail}</p>
                <p className="text-xs text-muted-foreground">{meta.hint}</p>
                {meta.key === 'conversion' ? <ConversionDate view={view} staff={staff} /> : null}
                {meta.key === 'review' ? <Review view={view} staff={staff} /> : null}
              </div>
              <StepActions
                step={step}
                to={meta.to}
                staff={staff}
                started={view.status !== 'not_started'}
                finished={view.status === 'finished'}
              />
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function StepActions({
  step,
  to,
  staff,
  started,
  finished,
}: {
  step: StepView;
  to: string | undefined;
  staff: boolean;
  started: boolean;
  finished: boolean;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const mark = useMutation({
    mutationFn: (status: 'done' | 'skipped' | 'open') =>
      api<MigrationView>(`/orgs/${orgId}/migration/steps/${step.key}`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      }),
    onSuccess: (v) => qc.setQueryData(['migration', orgId], v),
    onError: (e) => toast((e as Error).message, { tone: 'error' }),
  });
  const importStep = ['opening_balances', 'open_invoices', 'unpaid_bills'].includes(step.key);
  const blocked = !started || (finished && importStep);
  const label =
    step.key === 'parallel_run'
      ? 'Compare'
      : step.status === 'done' && importStep
        ? 'Review'
        : 'Import';
  return (
    <div className="flex shrink-0 items-start gap-2">
      {to ? (
        blocked && step.key !== 'parallel_run' ? (
          <Button
            variant="outline"
            size="sm"
            disabled
            title={finished ? 'The move is finished' : 'Set the conversion date first'}
          >
            {label}
          </Button>
        ) : (
          <Button variant={step.status === 'done' ? 'outline' : 'default'} size="sm" asChild>
            <Link to={to}>{label}</Link>
          </Button>
        )
      ) : null}
      {staff && started && !finished && MARKABLE.includes(step.key) ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" aria-label={`More for ${step.key}`}>
              <ChevronDown className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => mark.mutate('done')}>Mark as done</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => mark.mutate('skipped')}>
              Skip (nothing to bring over)
            </DropdownMenuItem>
            {step.manual ? (
              <DropdownMenuItem onSelect={() => mark.mutate('open')}>Reopen</DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}

function ConversionDate({ view, staff }: { view: MigrationView; staff: boolean }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [date, setDate] = useState(view.conversionDate ?? '');
  const [editing, setEditing] = useState(!view.conversionDate);
  const save = useMutation({
    mutationFn: () =>
      api<MigrationView>(`/orgs/${orgId}/migration`, {
        method: 'PUT',
        body: JSON.stringify({ conversionDate: date }),
      }),
    onSuccess: (v) => {
      qc.setQueryData(['migration', orgId], v);
      setEditing(false);
      toast('Conversion date saved');
    },
  });
  const fixed = Boolean(view.trialBalance) || view.status === 'finished';
  if (!staff) return null;
  if (!editing)
    return fixed ? null : (
      <div>
        <Button variant="link" className="h-auto p-0 text-sm" onClick={() => setEditing(true)}>
          Change the conversion date
        </Button>
      </div>
    );
  const notFirst = date !== '' && !date.endsWith('-01');
  return (
    <div className="mt-2 flex flex-col gap-2">
      <ErrorBanner>{save.error?.message}</ErrorBanner>
      <div className="flex items-end gap-2">
        <label className="flex flex-col gap-1 text-sm font-medium">
          Conversion date
          <DateInput
            value={date}
            onChange={setDate}
            aria-label="Conversion date"
            className="w-40"
            aria-invalid={notFirst}
          />
        </label>
        <Button
          size="sm"
          disabled={!date || notFirst || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
      {notFirst ? (
        <p className="text-xs text-destructive">
          Use the first day of a month, such as 01/07/2026.
        </p>
      ) : null}
    </div>
  );
}

function Review({ view, staff }: { view: MigrationView; staff: boolean }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const finish = useMutation({
    mutationFn: () =>
      api<MigrationView>(`/orgs/${orgId}/migration/finish`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: (v) => {
      qc.setQueryData(['migration', orgId], v);
      void qc.invalidateQueries({ queryKey: ['periods', orgId] });
      toast(`Finished. The books are locked through ${formatDate(v.openingDate!)}.`);
    },
  });
  if (view.status === 'not_started') return null;
  return (
    <div className="mt-2 flex flex-col gap-3">
      <table className="w-full max-w-xl text-sm" aria-label="Tie-out">
        <thead>
          <tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-1 font-medium">Control account</th>
            <th className="py-1 text-right font-medium">Trial balance</th>
            <th className="py-1 text-right font-medium">Open items</th>
            <th className="py-1 text-right font-medium">Difference</th>
          </tr>
        </thead>
        <tbody>
          {(['receivable', 'payable'] as Side[]).map((k) => {
            const t = view.tieOut[k];
            const off = (toMinor(t.difference) ?? 0n) !== 0n;
            return (
              <tr key={k} className={cn('border-b', off && 'bg-warning/10')}>
                <td className="py-1">
                  {SIDE[k].control} ({SIDE[k].title.toLowerCase()})
                </td>
                <td className="tnum py-1 text-right">{fmtAmount(t.expected)}</td>
                <td className="tnum py-1 text-right">{fmtAmount(t.imported)}</td>
                <td className={cn('tnum py-1 text-right', off && 'font-semibold')}>
                  {fmtAmount(t.difference)}
                  {off ? <span className="sr-only"> (differs)</span> : null}
                </td>
              </tr>
            );
          })}
          <tr>
            <td className="py-1">
              Opening balance equity
              {view.openingDate ? ` at ${formatDate(view.openingDate)}` : ''}
            </td>
            <td />
            <td />
            <td className="tnum py-1 text-right">{fmtAmount(view.openingEquity)}</td>
          </tr>
        </tbody>
      </table>
      {view.warnings.map((w) => (
        <p key={w} className="text-sm text-warning">
          {w}
        </p>
      ))}
      <ErrorBanner>{finish.error?.message}</ErrorBanner>
      {staff && view.status !== 'finished' ? (
        <div className="flex items-center gap-3">
          <Button disabled={!view.canFinish || finish.isPending} onClick={() => finish.mutate()}>
            <Lock className="size-4" />
            {finish.isPending
              ? 'Finishing…'
              : `Finish and lock the books through ${view.openingDate ? formatDate(view.openingDate) : '…'}`}
          </Button>
          {!view.canFinish ? (
            <span className="text-xs text-muted-foreground">{view.blockers[0]}</span>
          ) : (
            <span className="text-xs text-muted-foreground">
              Needs close rights for this client.
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}
